import { Fragment, useState } from 'react';
import { useI18n, formatDay } from '../../i18n';
import { getOfficialsReport, getOfficialsSummary } from '../../api/client';
import TabBar from '../shared/TabBar';
import { ReportCoverageView, ReportQuality, ReportReview } from '../officials/OfficialsReport';
import ModalBackdrop from '../shared/ModalBackdrop';
import ModalHeader from '../shared/ModalHeader';
import ModalBody from '../shared/ModalBody';
import ModalFooter from '../shared/ModalFooter';
import ErrorState from '../shared/ErrorState';
import Skeleton from '../shared/Skeleton';
import { personName } from '../../utils/personName';
import { useLoad } from '../../hooks/useLoad';
import type { OfficialLoad, OfficialSeat } from '../../types';

const SEATS: OfficialSeat[] = ['ring_referee', 'side_judge', 'jury'];

type View = 'load' | 'quality' | 'review' | 'coverage';

/** #616 — the load of each official in one tournament: the seats on each
 *  bout that is not a Walk Over, done (scored) and to do (pending, current).
 *  For the managers of the tournament, the tournament_manager role, the jury
 *  role and an admin (the server checks it too). #647 — three more views:
 *  the report of the officials of the tournament (quality, bouts to review,
 *  coverage). The Load view stays the first view: the managers use it during
 *  an event. The report loads when the first of its views opens. */
export default function OfficialsSummaryModal({ tournamentId, onClose }: { tournamentId: string; onClose: () => void }) {
  const { t } = useI18n();
  const [view, setView] = useState<View>('load');
  // The report loads one time, when a report view first opens. The latch stays on
  // when the user goes back to the Load view, so the gate does not follow the view.
  const [reportOpened, setReportOpened] = useState(false);
  const wantsReport = view !== 'load';
  if (wantsReport && !reportOpened) setReportOpened(true);

  const reportLoad = useLoad(
    reportOpened ? () => getOfficialsReport(tournamentId).then(r => r.data) : null,
    [tournamentId],
    { context: 'OfficialsSummaryModal:report' },
  );
  const report = reportLoad.data ?? null;
  const summary = useLoad(
    () => getOfficialsSummary(tournamentId).then(r => r.data.officials),
    [tournamentId],
    { context: 'OfficialsSummaryModal:load' },
  );
  const rows = summary.loading || summary.error ? null : summary.data ?? null;

  const seatLabel: Record<OfficialSeat, string> = { ring_referee: t.bout.ringReferee, side_judge: t.bout.sideJudge, jury: t.bout.jury };
  const multiDay = !!rows && new Set(rows.flatMap(r => r.days.map(d => d.day))).size > 1;
  const cell = (l: OfficialLoad) => (
    <>
      <span className="font-semibold">{l.done}</span>
      {l.todo > 0 && <span className="text-gray-400"> +{l.todo}</span>}
    </>
  );

  return (
    <ModalBackdrop onClose={onClose} maxWidth="2xl" layout="panel" ariaLabel={t.officials.summaryTitle}>
      <ModalHeader title={t.officials.summaryTitle} />
      <ModalBody>
        <div className="mb-3">
          <TabBar testId="officials-report-tab" activeTab={view} onChange={id => setView(id as View)} tabs={[
            { id: 'load', label: t.officials.reportLoad },
            { id: 'quality', label: t.officials.reportQuality },
            { id: 'review', label: report ? `${t.officials.reportReview} (${report.review.length})` : t.officials.reportReview },
            { id: 'coverage', label: t.officials.reportCoverage },
          ]} />
        </div>
        {wantsReport ? (
          reportLoad.error ? <ErrorState onRetry={reportLoad.reload} compact />
            : !report ? <Skeleton.List shape="section" count={2} />
            : view === 'quality' ? <ReportQuality report={report} />
            : view === 'review' ? <ReportReview report={report} />
            : <ReportCoverageView report={report} />
        ) : summary.error ? <ErrorState onRetry={summary.reload} compact />
          : !rows ? <Skeleton.List shape="section" count={2} />
          : rows.length === 0 ? <p className="text-sm text-gray-400 text-center py-6">{t.officials.noSeats}</p>
          : (
            <>
              <p className="text-xs text-gray-500 mb-2">{t.officials.done} <span className="text-gray-400">+{t.officials.todo}</span></p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="officials-summary">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                      <th className="py-2 pr-2 font-medium">{t.common.name}</th>
                      {SEATS.map(s => <th key={s} className="py-2 px-1 sm:px-2 font-medium text-center whitespace-nowrap">{seatLabel[s]}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => (
                      <Fragment key={r.user_id}>
                        <tr data-testid="officials-summary-row" data-official-id={r.user_id} className="border-b border-gray-50">
                          <td className="py-2 pr-2 font-medium">{personName(r.first_name, r.last_name)}</td>
                          {SEATS.map(s => <td key={s} className="py-2 px-1 sm:px-2 text-center tabular-nums">{cell(r.total[s])}</td>)}
                        </tr>
                        {multiDay && r.days.map(d => (
                          <tr key={`${r.user_id}-${d.day}`} className="text-xs text-gray-500">
                            <td className="py-1 pr-2 pl-3">{d.day ? formatDay(d.day, 'EEE dd MMM') : t.officials.noDay}</td>
                            {SEATS.map(s => <td key={s} className="py-1 px-1 sm:px-2 text-center tabular-nums">{cell(d.seats[s])}</td>)}
                          </tr>
                        ))}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
      </ModalBody>
      <ModalFooter onCancel={onClose} cancelLabel={t.common.close} />
    </ModalBackdrop>
  );
}
